import {Injectable} from '@angular/core';
import {HttpClient} from "@angular/common/http";
import * as _ from 'lodash';

import {AppAlertService} from "../app-alert.service";
import {DatabaseService} from "../database.service";
import {ParametersService} from "../parameters.service";
import {SendDataService} from "../send-data.service";
import {SubtitlesService} from "../subtitles.service";
import {TilesService} from "../tiles";
import {UserService} from "../user.service";
import {
    AlarmTile,
    SelectedModuleTilesData,
    Tile,
    TileContainer,
    TileOrderData,
    TilesContainer
} from "../../models";
import {DoublePipe, MonthNamePipe, WeekDayPipe} from "../../shared";

@Injectable({
    providedIn: 'root'
})
export class SettingsTilesService {
    private selectedUdid: string = '';
    showTilesListLoader: boolean = false;
    showSendingDataLoader: boolean = false;
    tilesList: TilesContainer[] = [];

    constructor(
        private appAlert: AppAlertService,
        private database: DatabaseService,
        private doublePipe: DoublePipe,
        private http: HttpClient,
        private monthName: MonthNamePipe,
        private parameters: ParametersService,
        private sendData: SendDataService,
        private subtitles: SubtitlesService,
        private tiles: TilesService,
        private user: UserService,
        private weekDay: WeekDayPipe,
    ) {
    }

    setTiles(): void {
        let containers = this.getTilesFromTilesService();
        const hasContainers = this.checkContainers(containers);

        if (hasContainers) {
            containers.sort(this.sortByContainerOrder);
            containers = this.updateContainersOrder(containers);
        }

        containers = this.sortTiles(containers);
        containers = this.updateTilesOrder(containers);

        this.tilesList = containers;
    }

    setIcon(tile: Tile): string {
        switch (tile.type) {
            case 1:
                return '/assets/img/icons/thermometer.svg';
            case 2:
                return '/assets/img/icons/fire.svg';
            case 6:
            case 40:
                return `/assets/img/id_icons/${tile.params.iconId}.svg`;
            case 11: {
                switch (tile.params.iconId) {
                    case 9:
                        return '/assets/img/icons/firing_up.svg';
                    case 17:
                        return '/assets/img/icons/pump.svg';
                    case 101:
                        return '/assets/img/icons/feeder.svg';
                    default:
                        return `/assets/img/id_icons/${tile.params.iconId}.svg`;
                }
            }
            case 21:
                return '/assets/img/icons/pump.svg';
            case 22:
                return '/assets/img/icons/fan.svg';
            case 23:
            case 24:
                return '/assets/img/id_icons/100.svg';
            case 31:
                return '/assets/img/id_icons/1.svg';
            case 32:
                return '/assets/img/id_icons/15.svg';
            case 41:
                return '/assets/img/id_icons/77.svg'
            case 252:
                return '/assets/img/icons/opentherm-red.svg';
            default:
                return ''
        }
    }

    setTileName(tile: Tile): string {
        switch (tile.type) {
            case 1:
            case 11:
                return this.subtitles.setSubtitle(tile.params.txtId);
            case 2:
                return this.subtitles.setSubtitle(205);
            case 6:
                return this.subtitles.setSubtitle(tile.params.widget1.txtId);
            case 22:
                const txtId = typeof tile.params.txtId === 'number' ? tile.params.txtId : -1;
                return this.subtitles.setSubtitle(txtId) || this.subtitles.setSubtitle(459)
            case 21:
                return `${this.subtitles.setSubtitle(576)} ${tile.params.pumpNumber > 0 ? tile.params.pumpNumber : ''}`;
            case 23:
                return `${this.subtitles.setSubtitle(991)} ${tile.params.valveNumber > 0 ? tile.params.valveNumber : ''}`;
            case 24:
                return `${this.subtitles.setSubtitle(1408)} ${tile.params.valveNumber > 0 ? tile.params.valveNumber : ''}`;
            case 31:
                return this.subtitles.setSubtitle(961);
            case 32:
                return this.subtitles.setSubtitle(246);
            case 40:
                return this.subtitles.setSubtitle(tile.params.headerId);
            case 41:
                return `${this.weekDay.transform(tile.params.dayId)}, ${tile.params.day} ${
                    this.monthName.transform(this.subtitles.setSubtitle(1792), tile.params.month)
                    } ${tile.params.year}`;
            case 252:
                return this.subtitles.setSubtitle(3645);
            default:
                return ''
        }
    }

    setSecondTileName(tile: Tile): string {
        switch (tile.type) {
            case 6:
                return this.subtitles.setSubtitle(tile.params.widget2.txtId);
            case 21:
            case 32:
                return this.subtitles.setSubtitle(tile.params.txtId);
            case 40:
                return this.subtitles.setSubtitle(tile.params.statusId);
            case 41:
                return `${tile.params.hours}:${this.doublePipe.transform(tile.params.minutes)}`;
            default:
                return ''
        }
    }

    setIconBackground(tile: Tile): boolean {
        const hasBackgroundByType = [6, 23, 24, 31, 32, 40, 41].includes(tile.type);
        const relayBackground = tile.type === 11 && ![9, 17, 101].includes(tile.params.iconId);

        return hasBackgroundByType || relayBackground;
    }

    changeTileOrder(tile: Tile, direction: 'up' | 'down'): void {
        if (this.tilesList.length === 1 && this.tilesList[0].values === null) {
            this.swapTiles(0, tile, direction);
            return;
        }

        if (this.tilesList.length >= 1 && this.tilesList[0].values !== null) {
            const containerWithTile = this.tilesList.find(container =>
                container.elements.findIndex(element => element.id === tile.id) !== -1
            )
            const containerIndex = this.tilesList.findIndex(
                container => container.values.id === containerWithTile.values.id
            );

            this.swapTiles(containerIndex, tile, direction);
        }
    }

    changeContainerOrder(container: TilesContainer, direction: 'up' | 'down'): void {
        if (container.values.id === 0) return;

        const tilesListCopy = _.cloneDeep(this.tilesList);

        const containerIndex = tilesListCopy.findIndex(
            element => element.values.id === container.values.id
        );

        if (direction === 'up' && containerIndex > 0) {
            const prevContainer = tilesListCopy[containerIndex - 1];

            tilesListCopy[containerIndex - 1] = container;
            tilesListCopy[containerIndex] = prevContainer;
        }

        if (direction === 'down' && containerIndex < tilesListCopy.length - 1) {
            const prevContainer = tilesListCopy[containerIndex + 1];

            tilesListCopy[containerIndex + 1] = container;
            tilesListCopy[containerIndex] = prevContainer;
        }

        this.tilesList = tilesListCopy;
    }

    setDefaultOrder(): void {
        const hasContainers = this.checkContainers(this.tilesList);

        if (!hasContainers) {
            this.tilesList[0].elements.forEach(tile => {
                tile.orderId = null;
                tile.visibility = true;
            });
        } else {
            this.tilesList.forEach(container => {
                container.values.expanded = container.values.id === 0;
                container.values.orderId = null;

                container.elements.forEach(tile => {
                    tile.orderId = null;
                    tile.visibility = true;
                });
            })

            this.tilesList.sort(this.sortByContainerOrder);
        }

        this.tilesList = this.sortTiles(this.tilesList);

        this.sendTilesOrder(null, 1321);
    }

    saveTilesOrder = () => {
        const order = this.createAllTilesOrderString(this.tilesList);

        this.sendTilesOrder(order, 1320)
    }

    hasContainers = () => {
        return this.tilesList.length >= 1 && this.tilesList[0].values !== null;
    }

    setActivityClass(tile: Tile): string {
        const ACTIVE = 'settings-tile--active';
        const INACTIVE = 'settings-tile--inactive';
        const DEFAULT = 'settings-tile--default';

        switch (tile.type) {
            case 1:
            case 2:
            case 11:
            case 21:
            case 22:
            case 23:
            case 24:
                return tile.params.workingStatus ? ACTIVE : INACTIVE;
            case 6: {
                switch (tile.params.statusId) {
                    case 1:
                        return ACTIVE;
                    case 0:
                        return INACTIVE;
                    case 2:
                        return DEFAULT;
                    default:
                        return ''
                }
            }
            case 31:
                return tile.params.percentage > 0 ? ACTIVE : INACTIVE;
            case 32:
                return tile.params.percentage > 0 ? ACTIVE : DEFAULT;
            case 40:
            case 252:
                return DEFAULT;
            case 41:
                return ACTIVE;
            default:
                return '';
        }
    }

    sortByContainerOrder = (a: TilesContainer, b: TilesContainer): number => {
        a.values.orderId = a.values.orderId === null ? 999 : a.values.orderId;
        b.values.orderId = b.values.orderId === null ? 999 : b.values.orderId;

        if (a.values.orderId < b.values.orderId)
            return -1;
        if (a.values.orderId > b.values.orderId)
            return 1;
        return 0;
    }

    checkLastContainer(index: number): boolean {
        if (index === this.tilesList.length - 2) {
            const lastContainer = this.tilesList[index + 1];

            if (lastContainer.values.id === 0) return false;
        }

        return index !== this.tilesList.length - 1;
    }

    getCurrentSelectedModuleTiles(moduleIndex: number): void {
        const selectedModuleType = this.parameters.modules[moduleIndex].type;
        if (selectedModuleType === 'zones_controller'
            || selectedModuleType === 'zones_system_controller') {
            return;
        }

        this.clearTilesList();
        this.showTilesListLoader = true;

        const {udid} = this.parameters.modules[moduleIndex];
        this.selectedUdid = udid;

        this.http.get<SelectedModuleTilesData>(`/api/v1/users/${this.user.user_id}/modules/${udid}/tiles`)
            .subscribe((response) => {
                let orderedTiles = response.elements;

                if (response.tilesOrder !== null) {
                    const order = this.database.createTilesOrder(response.tilesOrder);

                    orderedTiles = response.elements.map((tile) => this.database.setTileOrder(tile, order));
                }

                this.tiles.getTiles(orderedTiles);
                this.setTiles();
                this.showTilesListLoader = false;
            })
    }

    private clearTilesList(): void {
        this.tilesList = [];
    }

    private sendTilesOrder(order: string | null, messageId: number): void {
        this.showSendingDataLoader = true;

        const hasContainers = this.checkContainers(this.tilesList);
        const selectedUdid = this.selectedUdid
            || this.parameters.modules[this.parameters.selectedSettingsModuleId].udid;

        const data: TileOrderData = {
            order,
            type: hasContainers ? "containers" : "tiles"
        }

        this.sendData.sendTilesOrder(data, selectedUdid).subscribe(() => {
            this.selectedUdid = '';
            this.database.updateTilesOrder(data.order);
            this.showSendingDataLoader = false;
            this.appAlert.openAlertDialog({
                title: this.subtitles.setSubtitle(1253),
                message: this.subtitles.setSubtitle(messageId),
                okButtonText: this.subtitles.setSubtitle(475),
            })
        });
    }

    private createAllTilesOrderString(containers: TilesContainer[]): string {
        let order = '';
        const hasContainers = this.checkContainers(containers);

        if (hasContainers) {
            containers.forEach((container) => {
                let containerData = `${container.values.id}${container.values.expanded ? '|1|1,' : '|1|0,'}`;

                container.elements.forEach(tile => {
                    containerData += `${tile.id}${tile.visibility ? '|1,' : '|0,'}`;
                });

                order += containerData;
            })
        } else {
            containers[0].elements.forEach(tile => {
                order += `${tile.id}${tile.visibility ? '|1,' : '|0,'}`;
            });
        }

        return order;
    }

    private swapTiles(containerIndex: number, tile: Tile, direction: 'up' | 'down'): void {
        const tileIndex = this.tilesList[containerIndex].elements.findIndex(element => element.id === tile.id);

        if (direction === 'up' && tileIndex > 0) {
            const prevTile = this.tilesList[containerIndex].elements[tileIndex - 1];
            const prevOrderId = prevTile.orderId;

            prevTile.orderId = tile.orderId;
            tile.orderId = prevOrderId;

            this.tilesList[containerIndex].elements[tileIndex - 1] = tile;
            this.tilesList[containerIndex].elements[tileIndex] = prevTile;
        }

        if (direction === 'down' && tileIndex < this.tilesList[containerIndex].elements.length - 1) {
            const prevTile = this.tilesList[containerIndex].elements[tileIndex + 1];
            const prevOrderId = prevTile.orderId;

            prevTile.orderId = tile.orderId;
            tile.orderId = prevOrderId;

            this.tilesList[containerIndex].elements[tileIndex + 1] = tile;
            this.tilesList[containerIndex].elements[tileIndex] = prevTile;
        }
    }

    private updateTilesOrder(containers: TilesContainer[]): TilesContainer[] {
        let tileOrder = 0;

        return containers.map(container => {
            tileOrder = 0;
            return {
                ...container,
                elements: container.elements.map(tile => {
                    if (tile.orderId !== 999) {
                        tileOrder = tile.orderId;
                        return tile;
                    }

                    tileOrder += 1;
                    return {
                        ...tile,
                        orderId: tileOrder
                    }
                })
            }
        })
    }

    private checkContainers(containers: TilesContainer[]): boolean {
        return containers.length >= 1 && containers[0].values !== null;
    }

    private sortTiles(containers: TilesContainer[]): TilesContainer[] {
        return containers.map(container => ({
            ...container,
            elements: container.elements.sort(this.sortTilesByOrder.bind(this))
        }))
    }

    private updateContainersOrder(containers: TilesContainer[]): TilesContainer[] {
        let orderId = 0;

        return containers.map(tile => {
            if (tile.values.orderId !== 999) {
                orderId = tile.values.orderId;

                return tile;
            }

            orderId += 1;
            return {
                ...tile,
                values: {
                    ...tile.values,
                    orderId
                }
            }
        })
    }

    private getTilesFromTilesService(): TilesContainer[] {
        const tilesCopy = _.cloneDeep<Array<Tile | AlarmTile>>(this.tiles.withoutContainer.elements);

        if (this.tiles.createContainer) {
            const containerCopy = _.cloneDeep<TilesContainer[] | {}>(this.tiles.container);

            if (!Array.isArray(containerCopy)) {
                return [];
            }

            return this.getContainersWithTiles(containerCopy, tilesCopy)
        } else {
            return this.getTilesWithoutContainers(tilesCopy);
        }
    }

    private getContainersWithTiles(containers: TilesContainer[] | {}, tilesWithoutContainers: Array<Tile | AlarmTile>): TilesContainer[] {
        let containersWithTiles: TilesContainer[] = [];

        if (Array.isArray(containers)) {
            containersWithTiles = containers.map<TilesContainer>(({
                                                                      values,
                                                                      elements
                                                                  }) => ({
                values,
                elements: this.getVisibleTiles(elements)
            }))
        }

        if (tilesWithoutContainers.length > 0) {
            const updatedTiles = tilesWithoutContainers.map(tile => ({
                ...tile,
                orderId: 999
            }));
            const otherContainerOrder = this.database.tilesOrder.find(order => order.id === 0);

            const otherContainer: TileContainer = {
                expanded: otherContainerOrder ? otherContainerOrder.expanded : true,
                id: 0,
                orderId: 999,
                params: {
                    iconId: 72,
                    txtId: 307,
                    description: '',
                },
                type: 60,
                visibility: true,
                menuId: -1,
                parentId: -1,
                count: -1,
            }

            const othersContainer: TilesContainer = {
                values: otherContainer,
                elements: this.getVisibleTiles(updatedTiles)
            };

            containersWithTiles.push(othersContainer);
        }

        return containersWithTiles;
    }

    private getTilesWithoutContainers(tiles: Array<Tile | AlarmTile>): TilesContainer[] {
        return [{
            values: null,
            elements: this.getVisibleTiles(tiles)
        }]
    }

    private getVisibleTiles(tiles: Array<Tile | AlarmTile>): Tile[] {
        const hiddenTileTypes = [3, 50, 51, 'alarm'];

        const filteredTiles = tiles.filter(({type}) => !hiddenTileTypes.includes(type))

        return filteredTiles as Tile[];
    }

    private sortTilesByOrder(a: Tile, b: Tile): number {
        a.orderId = a.orderId == null ? 999 : a.orderId;
        b.orderId = b.orderId == null ? 999 : b.orderId;

        if (a.orderId === 999 && b.orderId === 999) {
            return this.database.sortTilesByTypeAndId(a, b)
        }

        if (a.orderId < b.orderId)
            return -1;
        if (a.orderId > b.orderId)
            return 1;
        return 0;
    }
}
